import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';

import { AssistantTab } from '@core/models/assistant/assistant.model';

import {
  AssistantConversationStore,
  SENDER_TTL_MS,
  ScopeChange,
  Sender,
  isLive,
  userPart
} from './assistant-conversation-store';

const RENEW_MS = 3_000;
// Two tabs can both find no note and both write theirs; the later write wins once storage settles.
const CLAIM_SETTLE_MS = 50;
const ACTIVE_PREFIX = 'gfe.assistant.active.';
const DRAFT_PREFIX = 'gfe.assistant.draft.';

export interface SyncScope {
  userName: string;
  dataSpaceId: number;
}

export type SyncChange = ScopeChange | { kind: 'expired'; id: string };

export interface SyncHooks {
  tabs: () => AssistantTab[];
  changed: (change: SyncChange) => void;
  /** Files whose content storage dropped to make room, which the page must drop too. */
  dropped: (fileIds: string[]) => void;
  /** False once the scope no longer belongs to the signed-in user, before the page has caught up. */
  valid: () => boolean;
}

/**
 * Keeps one browser tab's assistant conversations in step with the other tabs of the same user and
 * data space, through localStorage and its `storage` event, and lets only one tab send in a
 * conversation at a time.
 */
@Injectable({ providedIn: 'root' })
export class AssistantTabsSync {
  private readonly store = inject(AssistantConversationStore);
  /** Identifies this page load, so a refreshed tab never takes an old note for its own. */
  readonly pageId = crypto.randomUUID();

  private scope: SyncScope | null = null;
  private hooks: SyncHooks = {
    tabs: () => [],
    changed: () => undefined,
    dropped: () => undefined,
    valid: () => false
  };
  // The object last written or received per conversation; tab state is immutable, so a new object means a change.
  private readonly written = new Map<string, AssistantTab>();
  private readonly sending = new Set<string>();
  private renewTimer: ReturnType<typeof setInterval> | undefined;
  private readonly expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly notes = signal<ReadonlyMap<string, Sender>>(new Map());
  private readonly clock = signal(Date.now());

  readonly busyElsewhere = computed<ReadonlySet<string>>(() => {
    const now = this.clock();
    return new Set([...this.notes()].filter(([, s]) => isLive(s, now)).map(([id]) => id));
  });

  constructor() {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage || e.storageArea === null)
        this.handleStorage(e.key, e.newValue);
    };
    window.addEventListener('storage', listener);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('storage', listener);
      this.leave(false);
    });
  }

  open(
    scope: SyncScope | null,
    hooks: SyncHooks
  ): { tabs: AssistantTab[]; activeId: string } | null {
    this.leave(false);
    this.scope = scope;
    this.hooks = hooks;
    if (!scope) return null;
    const { conversations, legacyActiveId } = this.store.load(
      scope.userName,
      scope.dataSpaceId
    );
    for (const c of conversations) {
      this.written.set(c.tab.id, c.tab);
      this.note(c.tab.id, c.sender);
    }
    if (conversations.length === 0) return null;
    const remembered = readSession(ACTIVE_PREFIX, scope) ?? legacyActiveId;
    const activeId = conversations.some((c) => c.tab.id === remembered)
      ? remembered!
      : [...conversations].sort((a, b) => b.updatedAt - a.updatedAt)[0].tab.id;
    return { tabs: conversations.map((c) => c.tab), activeId };
  }

  /** Stops following the current scope; `write` saves this tab's changes and releases its notes first. */
  leave(write: boolean): void {
    if (write && this.scope) {
      const tabs = this.hooks.tabs();
      this.save(tabs, readSession(ACTIVE_PREFIX, this.scope) ?? '');
      this.releaseAll();
    }
    clearInterval(this.renewTimer);
    this.renewTimer = undefined;
    for (const timer of this.expiryTimers.values()) clearTimeout(timer);
    this.expiryTimers.clear();
    this.sending.clear();
    this.written.clear();
    this.notes.set(new Map());
    this.scope = null;
  }

  save(tabs: AssistantTab[], activeId: string): void {
    const scope = this.scope;
    if (!scope || !this.hooks.valid()) return;
    if (activeId) writeSession(ACTIVE_PREFIX, scope, activeId);
    const busy = this.busyElsewhere();
    const ids = new Set(tabs.map((t) => t.id));
    for (const listed of tabs) {
      // A write that made room may have dropped file contents from conversations listed after it.
      const t = this.current(listed.id) ?? listed;
      if (t.messages.length > 0 && !busy.has(t.id) && this.written.get(t.id) !== t)
        this.write(t);
    }
    for (const id of [...this.written.keys()])
      if (!ids.has(id) && !busy.has(id)) {
        this.store.markDeleted(scope.userName, scope.dataSpaceId, id);
        this.written.delete(id);
      }
  }

  async claim(id: string): Promise<boolean> {
    const scope = this.scope;
    const tab = this.current(id);
    if (!scope || !tab) return true;
    if (this.busyElsewhere().has(id)) return false;
    const found = this.store.read(scope.userName, scope.dataSpaceId, id);
    if (found?.sender && found.sender.pageId !== this.pageId && isLive(found.sender)) {
      this.note(id, found.sender);
      return false;
    }
    this.sending.add(id);
    this.write(tab);
    this.startRenewing();
    if (found) {
      await new Promise((resolve) => setTimeout(resolve, CLAIM_SETTLE_MS));
      const back = this.store.read(scope.userName, scope.dataSpaceId, id);
      if (this.scope !== scope) return false;
      // Closed while settling: a reply sent now would run with nowhere to show it.
      if (!this.current(id)) {
        this.stopSending(id);
        return false;
      }
      if (back?.sender && back.sender.pageId !== this.pageId) {
        this.stopSending(id);
        this.note(id, back.sender);
        return false;
      }
    }
    return true;
  }

  release(id: string): void {
    if (!this.stopSending(id)) return;
    const tab = this.current(id);
    if (tab) this.write(tab);
  }

  releaseAll(): void {
    for (const id of [...this.sending]) this.release(id);
  }

  draft(): string {
    return this.scope ? (readSession(DRAFT_PREFIX, this.scope) ?? '') : '';
  }

  rememberDraft(text: string): void {
    if (this.scope) writeSession(DRAFT_PREFIX, this.scope, text);
  }

  handleStorage(key: string | null, newValue: string | null): void {
    const scope = this.scope;
    if (!scope) return;
    const change = this.store.change(scope.userName, scope.dataSpaceId, key, newValue);
    if (!change) return;
    if (change.kind === 'upsert') {
      const { tab, sender } = change.conversation;
      if (this.sending.has(tab.id)) return;
      this.written.set(tab.id, tab);
      this.note(tab.id, sender);
    } else if (change.kind === 'delete') {
      // Closing is disabled while another tab sends, so a deletion here comes from logout or a full
      // storage: the reply stops rather than writing the conversation back.
      this.stopSending(change.id);
      this.written.delete(change.id);
      this.note(change.id, undefined);
    }
    this.hooks.changed(change);
  }

  private stopSending(id: string): boolean {
    const was = this.sending.delete(id);
    if (this.sending.size === 0) {
      clearInterval(this.renewTimer);
      this.renewTimer = undefined;
    }
    return was;
  }

  private current(id: string): AssistantTab | undefined {
    return this.hooks.tabs().find((t) => t.id === id);
  }

  private write(tab: AssistantTab): void {
    if (!this.scope || !this.hooks.valid()) return;
    const sender = this.sending.has(tab.id)
      ? { pageId: this.pageId, renewedAt: Date.now() }
      : undefined;
    const dropped = this.store.write(
      this.scope.userName,
      this.scope.dataSpaceId,
      tab,
      sender
    );
    this.written.set(tab.id, tab);
    if (dropped.length > 0) this.hooks.dropped(dropped);
  }

  private startRenewing(): void {
    this.renewTimer ??= setInterval(() => {
      for (const id of this.sending) {
        const tab = this.current(id);
        if (tab) this.write(tab);
      }
    }, RENEW_MS);
  }

  private note(id: string, sender: Sender | undefined): void {
    clearTimeout(this.expiryTimers.get(id));
    this.expiryTimers.delete(id);
    const notes = new Map(this.notes());
    if (sender && sender.pageId !== this.pageId && isLive(sender)) {
      notes.set(id, sender);
      this.expiryTimers.set(
        id,
        setTimeout(
          () => this.expire(id, sender),
          sender.renewedAt + SENDER_TTL_MS - Date.now()
        )
      );
    } else notes.delete(id);
    this.notes.set(notes);
    this.clock.set(Date.now());
  }

  private expire(id: string, sender: Sender): void {
    this.expiryTimers.delete(id);
    this.clock.set(Date.now());
    if (this.notes().get(id) !== sender) return;
    const notes = new Map(this.notes());
    notes.delete(id);
    this.notes.set(notes);
    this.hooks.changed({ kind: 'expired', id });
  }
}

function sessionKey(prefix: string, scope: SyncScope): string {
  return `${prefix}${userPart(scope.userName)}.${scope.dataSpaceId}`;
}

function readSession(prefix: string, scope: SyncScope): string | null {
  try {
    return sessionStorage.getItem(sessionKey(prefix, scope));
  } catch {
    return null;
  }
}

function writeSession(prefix: string, scope: SyncScope, value: string): void {
  try {
    if (value) sessionStorage.setItem(sessionKey(prefix, scope), value);
    else sessionStorage.removeItem(sessionKey(prefix, scope));
  } catch {
    // Storage unavailable: the selection and draft then live only in memory.
  }
}
